import { NODE_ENV } from '@/constants/environment';
import { datadogLogs, type LogsInitConfiguration } from '@datadog/browser-logs';
import {
  datadogRum,
  type PropagatorType,
  type RumInitConfiguration,
} from '@datadog/browser-rum';
import { getCurrentCorrelationId } from './correlationId';

type DatadogSite =
  | 'datadoghq.com'
  | 'datadoghq.eu'
  | 'us3.datadoghq.com'
  | 'us5.datadoghq.com'
  | 'ddog-gov.com';

const DEFAULT_SITE: DatadogSite = 'datadoghq.com';

/** Canonical env for Datadog tags and breedlogic hostnames (subdomain prefix). */
const DATADOG_ENV = {
  DEVELOPMENT: 'development',
  STAGING: 'staging',
  PRODUCTION: 'production',
} as const;
type DatadogCanonicalEnv = (typeof DATADOG_ENV)[keyof typeof DATADOG_ENV];

/** Azure App Service hostname segment: as-bl-*-{dev|test|prod}-aue-01 */
const AZURE_ENV_SLUG = {
  DEV: 'dev',
  TEST: 'test',
  PROD: 'prod',
} as const;
type AzureAppserviceEnvSlug =
  (typeof AZURE_ENV_SLUG)[keyof typeof AZURE_ENV_SLUG];

/** Localhost aliases accepted as raw env input (also used as the local RUM env tag). */
const LOCAL_ENV_ALIAS = {
  LOCAL: 'local',
  LOCALHOST: 'localhost',
} as const;

/**
 * Maps build-time env strings to a canonical Datadog `env` and to Azure URL slugs.
 * Accepts common aliases so Key Vault / CI can use either style.
 */
function resolveDeploymentEnvironment(): {
  /** Breedlogic subdomain / trace URL matching (e.g. development.*); not always the RUM `env` tag on localhost. */
  canonicalEnv: DatadogCanonicalEnv;
  /** Segment in *.azurewebsites.net hostnames. */
  azureSlug: AzureAppserviceEnvSlug;
} {
  const fromNext = process.env.NEXT_PUBLIC_ENV?.trim().toLowerCase();
  const fromNode =
    process.env.NODE_ENV === NODE_ENV.PRODUCTION
      ? DATADOG_ENV.PRODUCTION
      : undefined;
  const raw = (fromNext || fromNode || DATADOG_ENV.DEVELOPMENT).toLowerCase();

  if (raw === DATADOG_ENV.PRODUCTION || raw === AZURE_ENV_SLUG.PROD) {
    return {
      canonicalEnv: DATADOG_ENV.PRODUCTION,
      azureSlug: AZURE_ENV_SLUG.PROD,
    };
  }
  if (raw === DATADOG_ENV.STAGING || raw === AZURE_ENV_SLUG.TEST) {
    return {
      canonicalEnv: DATADOG_ENV.STAGING,
      azureSlug: AZURE_ENV_SLUG.TEST,
    };
  }
  if (
    raw === DATADOG_ENV.DEVELOPMENT ||
    raw === AZURE_ENV_SLUG.DEV ||
    raw === LOCAL_ENV_ALIAS.LOCAL ||
    raw === LOCAL_ENV_ALIAS.LOCALHOST
  ) {
    return {
      canonicalEnv: DATADOG_ENV.DEVELOPMENT,
      azureSlug: AZURE_ENV_SLUG.DEV,
    };
  }

  return {
    canonicalEnv: DATADOG_ENV.DEVELOPMENT,
    azureSlug: AZURE_ENV_SLUG.DEV,
  };
}

// Use a global variable to track initialization
let isDatadogInitialized = false;

// Add safeguards to prevent multiple initialization
function isDatadogExplicitlyDisabled(value: string | undefined): boolean {
  if (value === undefined || value === '') return true;
  const v = value.trim().toLowerCase();
  return v === 'false' || v === '0' || v === 'no' || v === 'off';
}

function isDatadogTruthy(value: string | undefined): boolean {
  if (value === undefined || value === '') return false;
  const v = value.trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes' || v === 'on';
}

/** Hostnames where we skip RUM/logs by default (browser sends to DD without a local agent). */
function isLocalDevMachineHostname(hostname: string): boolean {
  const h = hostname.trim().toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h === '::1' || h === '[::1]';
}

export const initializeDatadog = (): void => {
  // Check for initialization status in both module scope and window object
  if (
    isDatadogInitialized ||
    (typeof window !== 'undefined' &&
      (window as any).__DATADOG_INITIALIZED__) ||
    isDatadogExplicitlyDisabled(process.env.NEXT_PUBLIC_DATADOG_ENABLED)
  ) {
    return;
  }

  if (
    typeof window !== 'undefined' &&
    isLocalDevMachineHostname(window.location.hostname) &&
    !isDatadogTruthy(process.env.NEXT_PUBLIC_DATADOG_SEND_FROM_LOCALHOST)
  ) {
    return;
  }

  // Mark as initialized early to prevent race conditions
  isDatadogInitialized = true;
  if (typeof window !== 'undefined') {
    (window as any).__DATADOG_INITIALIZED__ = true;
  }

  // Validate required configuration
  if (!process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN) {
    console.error('Datadog client token is missing!');
    return;
  }

  const site = (process.env.NEXT_PUBLIC_DATADOG_SITE ||
    DEFAULT_SITE) as DatadogSite;
  const { canonicalEnv, azureSlug } = resolveDeploymentEnvironment();
  // RUM/logs `env` tag: use `local` on developer localhost so it is distinct from hosted `development`.
  const rumOnLocalhost =
    typeof window !== 'undefined' &&
    isLocalDevMachineHostname(window.location.hostname) &&
    isDatadogTruthy(process.env.NEXT_PUBLIC_DATADOG_SEND_FROM_LOCALHOST);
  const ddEnvTag: string = rumOnLocalhost
    ? LOCAL_ENV_ALIAS.LOCAL
    : canonicalEnv;
  // Datadog tags must be lowercase alphanumerics, underscores, hyphens, periods (see tagging docs).
  const serviceName = (
    process.env.NEXT_PUBLIC_DATADOG_SERVICE_NAME || 'frontend'
  )
    .trim()
    .toLowerCase();

  // Version handling - protect against undefined values
  const version = [
    process.env.version || '0',
    process.env.NEXT_PUBLIC_PRODUCTION_BUILD_NUMBER || '0',
    process.env.NEXT_PUBLIC_STAGING_BUILD_NUMBER || '0',
    process.env.NEXT_PUBLIC_DEV_BUILD_NUMBER || '0',
  ].join('.');

  const commonContext = {
    CorrelationId: getCurrentCorrelationId() || 'not-set',
    env: ddEnvTag,
    service: serviceName,
    version,
  };

  const logsConfig: LogsInitConfiguration = {
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || '',
    site,
    forwardErrorsToLogs: true,
    // error + warn only — log/info/debug are stripped in prod by
    // next.config.js `removeConsole`, so forwarding 'log' would be dead.
    forwardConsoleLogs: ['error', 'warn'],
    forwardReports: 'all',
    sessionSampleRate: 100,
    service: serviceName,
    env: ddEnvTag,
    version,
    beforeSend: (event) => {
      event.properties = {
        ...(event.properties || {}),
        CorrelationId: getCurrentCorrelationId() || 'not-set',
      };
      return true;
    },
  };

  const envEscaped = canonicalEnv.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const azureSlugEscaped = azureSlug.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Use the fully-escaped env (covers backslashes and all regex metachars,
  // not just dots) when injecting it into the regex source below.
  const envPrefixRegex =
    canonicalEnv === NODE_ENV.PRODUCTION ? '' : `${envEscaped}\\.`;

  // Canonical hostnames: https://development.* / https://staging.* (trace URL matching)
  const breedlogicRegex = new RegExp(
    `^https:\\/\\/${envPrefixRegex}([^.]+)\\.breedlogic\\.com\\.au`,
  );

  // Core/Auth APIs use short subdomains: https://dev.core…, https://test.core…
  let breedlogicShortLabelRegex: RegExp | null;
  if (canonicalEnv === DATADOG_ENV.DEVELOPMENT) {
    breedlogicShortLabelRegex = /^https:\/\/dev\.([^.]+)\.breedlogic\.com\.au/;
  } else if (canonicalEnv === DATADOG_ENV.STAGING) {
    breedlogicShortLabelRegex = /^https:\/\/test\.([^.]+)\.breedlogic\.com\.au/;
  } else {
    breedlogicShortLabelRegex = null;
  }

  // Apex: https://development.breedlogic.com.au / https://staging.breedlogic.com.au
  const breedlogicEnvApexRegex =
    canonicalEnv !== NODE_ENV.PRODUCTION
      ? new RegExp(`^https:\\/\\/${envEscaped}\\.breedlogic\\.com\\.au`)
      : null;

  let breedlogicShortApexRegex: RegExp | null;
  if (canonicalEnv === DATADOG_ENV.DEVELOPMENT) {
    breedlogicShortApexRegex = /^https:\/\/dev\.breedlogic\.com\.au/;
  } else if (canonicalEnv === DATADOG_ENV.STAGING) {
    breedlogicShortApexRegex = /^https:\/\/test\.breedlogic\.com\.au/;
  } else {
    breedlogicShortApexRegex = null;
  }

  // Matches: https://as-bl-<service>-{dev|test|prod}-aue-01.azurewebsites.net/*
  // azureSlug is always the real App Service segment (dev/test/prod), independent of
  // whether NEXT_PUBLIC_ENV was set to dev, development, test, staging, prod, or production.
  const azureRegexForEnv = new RegExp(
    `^https:\\/\\/as-bl-[a-z-]+-${azureSlugEscaped}-aue-01\\.azurewebsites\\.net`,
  );

  const rumTracePropagators: PropagatorType[] = ['tracecontext', 'datadog'];

  const allowedTracingUrls: RumInitConfiguration['allowedTracingUrls'] = [
    // Same-origin proxy routes (GraphQL + API) -- most traffic goes through here
    { match: /\/api\//, propagatorTypes: rumTracePropagators },
    { match: breedlogicRegex, propagatorTypes: rumTracePropagators },
    ...(breedlogicShortLabelRegex
      ? [
          {
            match: breedlogicShortLabelRegex,
            propagatorTypes: rumTracePropagators,
          },
        ]
      : []),
    ...(breedlogicEnvApexRegex
      ? [
          {
            match: breedlogicEnvApexRegex,
            propagatorTypes: rumTracePropagators,
          },
        ]
      : []),
    ...(breedlogicShortApexRegex
      ? [
          {
            match: breedlogicShortApexRegex,
            propagatorTypes: rumTracePropagators,
          },
        ]
      : []),
    { match: azureRegexForEnv, propagatorTypes: rumTracePropagators },
  ];

  const rumConfig: RumInitConfiguration = {
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID || '',
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || '',
    site,
    service: serviceName,
    env: ddEnvTag,
    version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: canonicalEnv === NODE_ENV.PRODUCTION ? 20 : 100,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input',
    traceSampleRate: 100,
    allowedTracingUrls,
    beforeSend: (event: any) => {
      event.properties = {
        ...(event.properties || {}),
        CorrelationId: getCurrentCorrelationId() || 'not-set',
      };
      return true;
    },
  };

  // Check if already initialized using SDK methods
  if (typeof window !== 'undefined' && !datadogLogs.getInitConfiguration()) {
    try {
      datadogLogs.init(logsConfig);
      datadogLogs.setGlobalContext(commonContext);
      console.log('Datadog Logs initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Datadog Logs:', error);
    }
  }

  if (typeof window !== 'undefined' && !datadogRum.getInitConfiguration()) {
    try {
      datadogRum.init(rumConfig);
      datadogRum.setGlobalContext(commonContext);

      if (canonicalEnv === NODE_ENV.PRODUCTION) {
        datadogRum.startSessionReplayRecording();
      }
      console.log('Datadog RUM initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Datadog RUM:', error);
    }
  }
};
